import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import dayjs from "dayjs";
import moment from "moment";
import cx from "classnames";
import Calendar from "./Calendar/Calendar";

import logo from "../../assets/logo.png";

import {
  fetchBookNowDetail,
  getBookNowDataWithDateObject,
  makeSlotAvailable,
  newBooking,
  verifyPayment,
} from "../../dux/bookNow";
import { Button } from "@mui/material";
import {
  getTotalCost,
  isSlotAvailable,
  mapStartTimeToIndex,
  getConsolidatedTurfNumber,
  checkSlotAvailabilityForDuration,
} from "../../helpers/bookNowHelper";
import styles from "./BookNow.module.css";
import { getLoginFlag } from "../../dux/login";
import { showPrompt } from "../../dux/prompt";
import { promptCategoryType } from "../../helpers/constants";
import { hidePrompt } from "../../dux/prompt";
import { getUser } from "../../helpers/sessionStorageHelper";
import { getCurrentTimeString } from "../../helpers/dateHelper";
import {
  apiCallComplete,
  isFetchBookNowPending,
  isNewBookingPending,
  isApiPending,
} from "../../dux/apiStatus";
import Loader from "../shared/UI/Loader/Loader";
import LoaderForButton from "../shared/UI/LoaderForButton";
import { getNewBookingFlag, resetBookingFlag } from "../../dux/ui/uiBookNow";
import { useNavigate } from "react-router-dom";
import { setTab } from "../../dux/myBookingTab";
import { getBookingTimeFrame } from "../../helpers/myBookingsHelper";
import { setBookNowDetail } from "../../dux/bookNow";
import { parseBookNowApiData } from "../../helpers/bookNowHelper";

const locationSportsMap = {
  srinagar: ["Football", "Box Cricket", "Cricket Net", "Pickle Ball"],
  // baramulla: ["Pickle Ball"],
  jammu: ["Pickle Ball"],
};

// Map frontend sport names to backend sport names
const sportMapping = {
  Football: "football",
  "Box Cricket": "box-cricket",
  "Cricket Net": "cricket-net",
  "Pickle Ball": "pickle-ball",
};

const fieldMappingAgainstSport = {
  "pickle-ball": "Court",
  football: "Turf",
  "box-cricket": "Turf",
  "cricket-net": "Net",
};

// Location-wise court mapping for each sport
// Defines which courts are available and their display names for each location
const locationCourtMapping = {
  srinagar: {
    "Pickle Ball": [
      { turfKey: "turfA", displayName: "Court A" },
      { turfKey: "turfB", displayName: "Court B" },
    ],
  },
  jammu: {
    "Pickle Ball": [
      { turfKey: "turfA", displayName: "Court 1" },
      { turfKey: "turfB", displayName: "Court 2" },
      { turfKey: "turfC", displayName: "Court 3" },
      { turfKey: "turfD", displayName: "Court 4" },
    ],
  },
};

// Helper function to get courts configuration for a location and sport
const getCourtsForLocation = (location, sport) => {
  const locationKey = location?.toLowerCase();
  if (!locationKey || !locationCourtMapping[locationKey]) {
    return [];
  }
  return locationCourtMapping[locationKey][sport] || [];
};

const BookNow = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { location } = useParams();
  const sportsList = locationSportsMap[location?.toLowerCase()] || [];

  const [duration, setDuration] = useState(1);
  const [date, setDate] = useState(dayjs());
  const [allSlots, setAllSlots] = useState([]);
  const [selectedSport, setSelectedSport] = useState(sportsList[0]);

  const [selectedSlot, setSelectedSlot] = useState("");
  const [selectedTurf, setSelectedTurf] = useState("");
  const [selectedLocation] = useState(location);

  // Equipment selection for Pickleball
  const [paddleQuantity, setPaddleQuantity] = useState(0);
  const [ballQuantity, setBallQuantity] = useState(0);
  const [loadingEquipment, setLoadingEquipment] = useState(false);

  const login = useSelector(getLoginFlag);
  const fetchingBookNowDetail = useSelector(isFetchBookNowPending);
  const newBookingInProgress = useSelector(isNewBookingPending);
  const newBookingHappened = useSelector(getNewBookingFlag);
  const checkingHolidayBooking = useSelector((state) =>
    isApiPending(state, "checkHolidayBooking"),
  );
  const { emailAddress, mobileNumber, firstName, lastName } = getUser();

  // Get equipment data from Redux state
  const { equipmentConfigs } = useSelector(getBookNowDataWithDateObject);

  // Code added by Anshul to handle different partial payments amount for Football/Box Cricket
  let amt = 505;
  let btn_partial_txt = "PAY PARTIAL (505/-)";

  if (selectedSport === "Football" || selectedSport === "Box Cricket") {
    if (duration === 2) {
      amt = 1010;
      btn_partial_txt = "PAY PARTIAL (1010/-)";
    } else if (duration === 3) {
      amt = 1515;
      btn_partial_txt = "PAY PARTIAL (1515/-)";
    } else if (duration === 4) {
      amt = 2020;
      btn_partial_txt = "PAY PARTIAL (2020/-)";
    }
  }

  const timeFormat = (timeString) => {
    const newTime = timeString.split(" - ");

    const timeString12hr = new Date(
      "1970-01-01T" + newTime[0] + "Z",
    ).toLocaleTimeString("en-US", {
      timeZone: "UTC",
      hour12: true,
      hour: "numeric",
      minute: "numeric",
    });

    const timeString12hr_ = new Date(
      "1970-01-01T" + newTime[1] + "Z",
    ).toLocaleTimeString("en-US", {
      timeZone: "UTC",
      hour12: true,
      hour: "numeric",
      minute: "numeric",
    });

    return `${timeString12hr} - ${timeString12hr_}`;
  };

  const disableBtn = () =>
    !(selectedSport && selectedSlot && duration && date && selectedTurf);

  const {
    bookingAllowed,
    slotsAvailability,
    slots,
    newBooking: newBookingData,
    holidayBookingStatus,
    datePresenceStatus,
    dayMappings,
    pricingConfigs,
    availableSlots,
  } = useSelector(getBookNowDataWithDateObject);

  // Process booking data when all Redux state is available
  useEffect(() => {
    if (availableSlots && pricingConfigs && !fetchingBookNowDetail) {
      try {
        // Parse the date string to a Date object for weekend detection
        let dateObj = null;
        if (date.format) {
          const dateStr = date.format("DD/MM/YYYY");
          const [day, month, year] = dateStr.split("/");
          dateObj = new Date(year, month - 1, day); // month is 0-indexed
        }

        const parsedData = parseBookNowApiData(
          availableSlots,
          pricingConfigs,
          dateObj,
          dayMappings,
          datePresenceStatus,
        );

        // Override bookingAllowed based on holiday status
        if (
          holidayBookingStatus &&
          holidayBookingStatus.booking_allowed === false
        ) {
          parsedData.bookingAllowed = false;
        }

        // Dispatch the processed data to Redux
        dispatch(setBookNowDetail(parsedData));
      } catch (error) {
        console.error("Error parsing book now data:", error);
        const fallbackData = {
          date: date.format("DD/MM/YYYY"),
          bookingAllowed: holidayBookingStatus
            ? holidayBookingStatus.booking_allowed !== false
            : true,
          slotsAvailability: {
            turfA: Array(48).fill(true),
            turfB: Array(48).fill(true),
            turfC: Array(48).fill(true),
            turfD: Array(48).fill(true),
            turfE: Array(48).fill(true),
          },
          slots: {
            turfA: [],
            turfB: [],
            turfC: [],
            turfD: [],
            turfE: [],
          },
        };
        dispatch(setBookNowDetail(fallbackData));
      }
    }
  }, [
    availableSlots,
    pricingConfigs,
    dayMappings,
    datePresenceStatus,
    holidayBookingStatus,
    fetchingBookNowDetail,
    date,
    dispatch,
  ]);

  const dateChangeHandler = (value) => {
    // Only dispatch fetchBookNowDetail - middleware will handle the sequential chain
    dispatch(
      fetchBookNowDetail({
        date: value.format("DD/MM/YYYY"),
        sport: sportMapping[selectedSport],
        location: selectedLocation,
      }),
    );
    setDate(value);
    setSelectedSlot("");
    setSelectedTurf("");
  };

  useEffect(() => {
    setSelectedTurf("");
    setSelectedSlot("");
    resetEquipmentSelection(); // Reset equipment selection when sport changes

    // Only dispatch fetchBookNowDetail - middleware will handle the sequential chain
    dispatch(
      fetchBookNowDetail({
        date: date.format("DD/MM/YYYY"),
        sport: sportMapping[selectedSport],
        location: selectedLocation,
      }),
    );

    // Equipment will be fetched by middleware for Pickle Ball
    // No need to call fetchEquipmentOptions here
  }, [selectedSport]);

  // Check for equipment data in Redux state when sport changes to Pickle Ball
  useEffect(() => {
    if (selectedSport === "Pickle Ball") {
      if (equipmentConfigs && equipmentConfigs.length > 0) {
        setLoadingEquipment(false);
      } else {
        setLoadingEquipment(true);
      }
    }
  }, [selectedSport, equipmentConfigs]);

  useEffect(() => {
    // Combine slots from all turfs to show all available slots
    const allAvailableSlots = [];

    if (slots && Array.isArray(slots.turfA)) {
      allAvailableSlots.push(...slots.turfA);
    }
    if (slots && Array.isArray(slots.turfB)) {
      allAvailableSlots.push(...slots.turfB);
    }
    if (slots && Array.isArray(slots.turfC)) {
      allAvailableSlots.push(...slots.turfC);
    }

    // Remove duplicates based on time and sort by time to ensure earliest slots are first
    const uniqueSlots = allAvailableSlots
      .filter(
        (slot, index, self) =>
          index === self.findIndex((s) => s.time === slot.time),
      )
      .sort((a, b) => {
        // Sort by time (HH:mm format)
        const timeA = a.time;
        const timeB = b.time;
        return timeA.localeCompare(timeB);
      });

    setAllSlots(uniqueSlots);
  }, [slots]);

  useEffect(() => {
    // Only redirect if this is a fresh booking completion, not on component mount
    if (newBookingHappened && !sessionStorage.getItem("bookingRedirected")) {
      sessionStorage.setItem("bookingRedirected", "true");
      dispatch(resetBookingFlag());
      dispatch(setTab({ tab: getBookingTimeFrame(date.format("DD/MM/YYYY")) }));
      // navigate("/my-account/bookings");
    } else {
    }
  }, [newBookingHappened, dispatch, navigate, date]);

  useEffect(() => {
    if (
      newBookingData?.order_id &&
      newBookingData.order_id !== "" &&
      sessionStorage.getItem("triggerPayment") === "true"
    ) {
      paymentHandler({
        order_id: newBookingData.order_id,
        amount: newBookingData.amount,
      });
      sessionStorage.removeItem("triggerPayment"); // Clear flag after handling
    }
  }, [newBookingData]);

  useEffect(() => {
    // Only dispatch fetchBookNowDetail - middleware will handle the sequential chain
    dispatch(
      fetchBookNowDetail({
        date: date.format("DD/MM/YYYY"),
        sport: sportMapping[selectedSport],
        location: selectedLocation,
      }),
    );
    return () => {
      dispatch(apiCallComplete({ api: "newBooking" }));
    };
  }, [date, dispatch, selectedLocation, selectedSport]);

  useEffect(() => {
    // Reset the booking flag on mount to prevent unwanted redirects
    dispatch(resetBookingFlag());

    // Clear the booking redirect flag when component mounts
    sessionStorage.removeItem("bookingRedirected");

    // Cleanup function to clear the flag when component unmounts
    return () => {
      sessionStorage.removeItem("bookingRedirected");
    };
  }, [dispatch]);

  const getTimeSlot = (time) =>
    `${time} - ${moment(time, ["h:m a", "H:m"])
      .add(1, "hour")
      .format("HH:mm")}`;

  const getFinalSlot = () =>
    selectedSlot
      ? `${selectedSlot} - ${moment(selectedSlot, ["h:m a", "H:m"])
          .add(duration, "hour")
          .format("HH:mm")}`
      : "Select Slot";

  const slotClickHandler = (selTime) => {
    setSelectedSlot(selTime);
    setSelectedTurf("");
    setAllSlots((prev) =>
      prev.map((i) =>
        i.time === selTime
          ? { ...i, selected: true }
          : { ...i, selected: false },
      ),
    );
  };

  // Equipment selection handlers for Pickleball
  const handlePaddleQuantityChange = (change) => {
    const newQuantity = paddleQuantity + change;
    if (newQuantity >= 0 && newQuantity <= 4) {
      setPaddleQuantity(newQuantity);
    }
  };

  const handleBallQuantityChange = (change) => {
    const newQuantity = ballQuantity + change;
    if (newQuantity >= 0 && newQuantity <= 1) {
      setBallQuantity(newQuantity);
    }
  };

  // Reset equipment selection when sport changes
  const resetEquipmentSelection = () => {
    setPaddleQuantity(0); // Minimum 0 paddle required
    setBallQuantity(0);
  };

  const getBaseCost = () => {
    if (!slots) {
      return 0;
    }

    // Only show pricing when a turf is selected
    if (selectedTurf === "") {
      return 0;
    }

    // If a specific turf is selected, show pricing for that turf
    if (!slots[selectedTurf]) {
      return 0;
    }

    const turfSlots = slots[selectedTurf];
    if (!Array.isArray(turfSlots) || turfSlots.length === 0) {
      return 0;
    }

    // If no slot is selected, show the minimum cost for this turf
    if (selectedSlot === "") {
      let minCost = Infinity;
      for (const slot of turfSlots) {
        if (slot && typeof slot.cost === "number" && slot.cost > 0) {
          minCost = Math.min(minCost, slot.cost);
        }
      }
      return minCost === Infinity ? 0 : minCost;
    }

    // If slot is selected, show the cost for that specific slot
    return getTotalCost(turfSlots, selectedSlot, duration);
  };

  const getEquipmentCost = () => {
    if (selectedSport !== "Pickle Ball") {
      return 0;
    }

    // Don't show equipment cost if no court is selected
    if (selectedTurf === "") {
      return 0;
    }

    let equipmentCost = 0;

    // Calculate paddle cost
    const paddleConfig = equipmentConfigs.find(
      (equipment) => equipment.name === "Paddle",
    );
    if (paddleConfig && paddleQuantity > 0) {
      equipmentCost += paddleQuantity * parseFloat(paddleConfig.unit_price);
    }

    // Calculate ball cost
    const ballConfig = equipmentConfigs.find(
      (equipment) => equipment.name === "Ball",
    );
    if (ballConfig && ballQuantity > 0) {
      equipmentCost += ballQuantity * parseFloat(ballConfig.unit_price);
    }

    return equipmentCost;
  };

  const totalCost = () => {
    const baseCost = getBaseCost();
    const equipmentCost = getEquipmentCost();
    const total = baseCost + equipmentCost;

    return total;
  };

  // Calculate maximum available duration for the selected slot
  const getMaxAvailableDuration = () => {
    if (!selectedSlot) return 1;

    let maxDuration = 1;
    const maxPossibleDuration = 4; // Maximum allowed duration

    // Check each turf for the maximum consecutive available duration
    const turfsToCheck = selectedTurf
      ? [selectedTurf]
      : (() => {
          switch (selectedSport) {
            case "Football":
            case "Box Cricket":
              return ["turfA", "turfB", "turfC"];
            case "Pickle Ball": {
              // Use location-based mapping for Pickle Ball
              const courts = getCourtsForLocation(
                selectedLocation,
                "Pickle Ball",
              );
              return courts.map((court) => court.turfKey);
            }
            case "Cricket Net":
              return ["turfA", "turfB", "turfD", "turfE"];
            default:
              return ["turfA", "turfB", "turfC"];
          }
        })();

    for (const turfKey of turfsToCheck) {
      if (
        slotsAvailability[turfKey] &&
        Array.isArray(slotsAvailability[turfKey])
      ) {
        for (
          let testDuration = 1;
          testDuration <= maxPossibleDuration;
          testDuration++
        ) {
          // Check both slot availability and pricing availability
          const slotCheck = checkSlotAvailabilityForDuration(
            slotsAvailability[turfKey],
            selectedSlot,
            testDuration,
          );

          if (slotCheck.available) {
            // Also check if pricing is available for all slots in this duration
            let pricingAvailable = true;
            const startIndex = mapStartTimeToIndex[selectedSlot];
            const lastIndex = startIndex + testDuration * 2 - 1;

            for (let i = startIndex; i <= lastIndex; i++) {
              const timeSlot = getTimeSlotByIndex(i);
              if (!isTurfPricingAvailable(turfKey, timeSlot)) {
                pricingAvailable = false;
                break;
              }
            }

            if (pricingAvailable) {
              maxDuration = Math.max(maxDuration, testDuration);
            } else {
              break; // Stop if pricing is not available
            }
          } else {
            break; // Stop checking this turf if duration is not available
          }
        }
      }
    }

    return maxDuration;
  };

  const durationBtnClickHandler = (v) => {
    const newDuration = duration + v;
    const maxDuration = getMaxAvailableDuration();

    // Only check for increasing duration (v > 0)
    if (v > 0) {
      if (newDuration > maxDuration) {
        dispatch(
          showPrompt({
            category: promptCategoryType.ERROR,
            message: `Maximum available duration is ${maxDuration} hour(s)`,
          }),
        );
        return;
      }

      let isAvailable = false;
      let errorReason = "";

      if (selectedSlot) {
        if (selectedTurf && slotsAvailability[selectedTurf]) {
          // If a turf is selected, check only that turf
          const turfCheck = checkSlotAvailabilityForDuration(
            slotsAvailability[selectedTurf],
            selectedSlot,
            newDuration,
          );
          if (turfCheck.available) {
            isAvailable = true;
          } else {
            errorReason = turfCheck.reason;
          }
        } else {
          // If no turf is selected, check all turfs
          const { turfA, turfB, turfC } = slotsAvailability;

          if (turfA && Array.isArray(turfA)) {
            const turfACheck = checkSlotAvailabilityForDuration(
              turfA,
              selectedSlot,
              newDuration,
            );
            if (turfACheck.available) {
              isAvailable = true;
            } else {
              errorReason = turfACheck.reason;
            }
          }

          if (!isAvailable && turfB && Array.isArray(turfB)) {
            const turfBCheck = checkSlotAvailabilityForDuration(
              turfB,
              selectedSlot,
              newDuration,
            );
            if (turfBCheck.available) {
              isAvailable = true;
            } else {
              errorReason = turfBCheck.reason;
            }
          }

          if (!isAvailable && turfC && Array.isArray(turfC)) {
            const turfCCheck = checkSlotAvailabilityForDuration(
              turfC,
              selectedSlot,
              newDuration,
            );
            if (turfCCheck.available) {
              isAvailable = true;
            } else {
              errorReason = turfCCheck.reason;
            }
          }
        }
      }

      if (isAvailable) {
        setDuration(newDuration);
      } else {
        dispatch(
          showPrompt({
            category: promptCategoryType.ERROR,
            message: `Cannot increase duration: ${errorReason}`,
          }),
        );
      }
    } else {
      // For decreasing duration, just update
      setDuration(newDuration);
    }
  };

  // Load Razorpay script dynamically
  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(window.Razorpay);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        resolve(window.Razorpay);
      };
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        reject(new Error("Failed to load Razorpay script"));
      };
      document.head.appendChild(script);
    });
  };

  const paymentHandler = async ({ amount, order_id }) => {
    // Validate required parameters
    if (!amount || !order_id) {
      dispatch(
        showPrompt({
          category: promptCategoryType.ERROR,
          message: "Payment initialization failed: Missing order details",
        }),
      );
      dispatch(apiCallComplete({ api: "newBooking" }));
      return;
    }

    try {
      // Load Razorpay script if not already loaded
      const Razorpay = await loadRazorpayScript();

      // Close any existing prompts to prevent modal conflicts
      dispatch(hidePrompt());

      // const hostUrl =
      //   window.location.hostname === "localhost"
      //     ? "http://localhost:3002"
      //     : "https://whssportshub.in";

      const r_key =
        window.location.hostname === "localhost"
          ? "rzp_test_SUrbNKtaSKvnQx"
          : "rzp_live_zonoIiPc15csvh";

      const options = {
        key: r_key,
        amount: amount * 100, // Amount is in currency subunits (paise)
        currency: "INR",
        name: "WHS sports hub",
        description: "Booking slot",
        image: logo,
        order_id,
        redirect: true,
        handler: function (response) {
          const { razorpay_order_id, razorpay_payment_id, razorpay_signature } =
            response;
          dispatch(apiCallComplete({ api: "newBooking" }));
          navigate("/my-account/bookings");
          dispatch(
            verifyPayment({
              razorpay_order_id,
              razorpay_payment_id,
              razorpay_signature,
              transactionId: newBookingData.transactionId,
              sport: sportMapping[selectedSport],
              turfNumber: getConsolidatedTurfNumber(
                sportMapping[selectedSport],
                selectedTurf,
                selectedLocation,
              ),
              slotDate: date.format("DD/MM/YYYY"),
              slotTime: selectedSlot,
              duration,
              paidAmount: amount,
              totalAmount: costWithExtraFee() * 100,
              location: selectedLocation,
            }),
          );
        },
        modal: {
          ondismiss: function () {
            sessionStorage.removeItem("triggerPayment");
            dispatch(
              makeSlotAvailable({
                turfNumber: getConsolidatedTurfNumber(
                  sportMapping[selectedSport],
                  selectedTurf,
                  selectedLocation,
                ),
                slotDate: date.format("DD/MM/YYYY"),
                slotTime: selectedSlot,
                sport: sportMapping[selectedSport],
                location: selectedLocation,
                duration: duration,
              }),
            );
            dispatch(apiCallComplete({ api: "newBooking" }));
          },
        },
        prefill: {
          name: `${firstName} ${lastName}`,
          email: emailAddress,
          contact: mobileNumber,
        },
        notes: {
          address: "WHS Sports Hub",
          booking_id: newBookingData?.booking_id,
          sport: sportMapping[selectedSport],
          location: selectedLocation,
          turf_number: getConsolidatedTurfNumber(
            sportMapping[selectedSport],
            selectedTurf,
            selectedLocation,
          ),
        },
        theme: {
          color: "#3399cc",
        },
      };

      const rzp1 = new Razorpay(options);

      rzp1.on("payment.failed", function (response) {
        dispatch(apiCallComplete({ api: "newBooking" }));
        dispatch(
          showPrompt({
            category: promptCategoryType.ERROR,
            message: "Payment failed! Please try again.",
          }),
        );
      });

      rzp1.open();
    } catch (error) {
      dispatch(
        showPrompt({
          category: promptCategoryType.ERROR,
          message: "Payment initialization failed: " + error.message,
        }),
      );
      dispatch(apiCallComplete({ api: "newBooking" }));
    }
  };

  const costWithExtraFee = () => Math.ceil((totalCost() * 101) / 100);

  const submitHandler = (partial) => {
    if (login) {
      sessionStorage.setItem("triggerPayment", "true");

      const bookingData = {
        sport: sportMapping[selectedSport],
        emailAddress,
        slotDate: date.format("DD/MM/YYYY"),
        slotTime: selectedSlot,
        duration,
        turfNumber: getConsolidatedTurfNumber(
          sportMapping[selectedSport],
          selectedTurf,
          selectedLocation,
        ),
        location: selectedLocation,
      };

      // Add equipment selections for Pickleball
      if (selectedSport === "Pickle Ball") {
        const equipmentSelections = [];

        // Find paddle equipment config
        const paddleConfig = equipmentConfigs.find(
          (equipment) => equipment.name === "Paddle",
        );
        if (paddleConfig && paddleQuantity > 0) {
          equipmentSelections.push({
            equipment_config_id: paddleConfig.id,
            quantity: paddleQuantity,
          });
        }

        // Find ball equipment config
        const ballConfig = equipmentConfigs.find(
          (equipment) => equipment.name === "Ball",
        );
        if (ballConfig && ballQuantity > 0) {
          equipmentSelections.push({
            equipment_config_id: ballConfig.id,
            quantity: ballQuantity,
          });
        }

        if (equipmentSelections.length > 0) {
          bookingData.equipment_selections = equipmentSelections;
        }
      }

      if (partial === 1) {
        bookingData.paidAmount = amt;
        bookingData.totalAmount = costWithExtraFee();
      } else {
        bookingData.paidAmount = costWithExtraFee();
        bookingData.totalAmount = costWithExtraFee();
      }

      dispatch(newBooking(bookingData));
    } else {
      dispatch(showPrompt({ category: promptCategoryType.LOGIN }));
    }
  };

  const isAnySlotAvailable = (index) => {
    // Helper function to check if a turf has both availability and pricing for a specific time
    const checkTurfAvailabilityAndPricing = (turfKey, timeIndex) => {
      const hasAvailability =
        slotsAvailability[turfKey] && slotsAvailability[turfKey][timeIndex];
      if (!hasAvailability) return false;

      // Get the time string for this index
      const timeString = Object.keys(mapStartTimeToIndex).find(
        (key) => mapStartTimeToIndex[key] === timeIndex,
      );
      if (!timeString) return false;

      // Check if this turf has pricing for this time
      const hasPricing =
        slots &&
        slots[turfKey] &&
        slots[turfKey].some((slot) => slot.time === timeString);
      return hasPricing;
    };

    switch (selectedSport) {
      case "Football":
      case "Box Cricket":
        return (
          checkTurfAvailabilityAndPricing("turfA", index) ||
          checkTurfAvailabilityAndPricing("turfB", index) ||
          checkTurfAvailabilityAndPricing("turfC", index)
        );

      case "Pickle Ball": {
        const courts = getCourtsForLocation(selectedLocation, "Pickle Ball");
        return courts.some((court) =>
          checkTurfAvailabilityAndPricing(court.turfKey, index),
        );
      }

      case "Cricket Net":
        return (
          checkTurfAvailabilityAndPricing("turfA", index) ||
          checkTurfAvailabilityAndPricing("turfB", index) ||
          checkTurfAvailabilityAndPricing("turfC", index) ||
          checkTurfAvailabilityAndPricing("turfD", index)
        );

      default:
        return false;
    }
  };

  // Check if a turf has pricing configured for the selected slot
  const isTurfPricingAvailable = (turfKey, timeSlot = null) => {
    const slotToCheck = timeSlot || selectedSlot;
    if (!slotToCheck || !slots || !slots[turfKey]) {
      return false;
    }

    // Check if the specified slot exists in the turf's pricing data
    const turfSlots = slots[turfKey];
    return turfSlots.some((slot) => slot.time === slotToCheck);
  };

  const shouldDisableSlotTime = (time) => {
    const currentTime = getCurrentTimeString();
    const index = mapStartTimeToIndex[time];
    const isToday = date.format("DD/MM/YYYY") === dayjs().format("DD/MM/YYYY");
    const isPastTime = currentTime > time;

    if ((isPastTime && isToday) || !isAnySlotAvailable(index)) {
      return true;
    }

    return false;
  };

  const btnStyle = {
    fontSize: "40px",
    backgroundColor: "rgb(240, 240, 240)",
    padding: 0,
    borderRadius: "50%",
    margin: "15px",
    cursor: "pointer",
    lineHeight: "65px",
  };
  // Helper function to get time slot by index
  const getTimeSlotByIndex = (index) => {
    return Object.keys(mapStartTimeToIndex).find(
      (key) => mapStartTimeToIndex[key] === index,
    );
  };

  // const getLastSlot = () => {
  //   if (slots && Array.isArray(slots.turfA) && slots.turfA.length !== 0) {
  //     const lastSlotTime = slots.turfA[slots.turfA.length - 1].time;
  //     return mapStartTimeToIndex[lastSlotTime] / 2;
  //   }
  //   return undefined;
  // };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.calendarContainer}>
          <div className={styles.sportContainer}>
            <h3 className={styles.sectionHeader}>Select Sports:</h3>

            <div className={styles.sportsCards}>
              {locationSportsMap[selectedLocation]?.includes("Football") && (
                <button
                  onClick={() => setSelectedSport("Football")}
                  className={cx(styles.sportCard, {
                    [styles.selectedSport]: selectedSport === "Football",
                  })}
                >
                  <div className={styles.footballSportCard} />
                  <span>Football</span>
                </button>
              )}

              {locationSportsMap[selectedLocation]?.includes("Box Cricket") && (
                <button
                  onClick={() => setSelectedSport("Box Cricket")}
                  className={cx(styles.sportCard, {
                    [styles.selectedSport]: selectedSport === "Box Cricket",
                  })}
                >
                  <div className={styles.cricketSportCard} />
                  <span>Box Cricket</span>
                </button>
              )}

              {locationSportsMap[selectedLocation]?.includes("Cricket Net") && (
                <button
                  onClick={() => {
                    setSelectedSport("Cricket Net");
                  }}
                  className={cx(styles.sportCard, {
                    [styles.selectedSport]: selectedSport === "Cricket Net",
                  })}
                >
                  <div className={styles.cricketSportCard} />
                  <span>Cricket Nets</span>
                </button>
              )}

              {locationSportsMap[selectedLocation]?.includes("Pickle Ball") && (
                <button
                  onClick={() => setSelectedSport("Pickle Ball")}
                  className={cx(styles.sportCard, {
                    [styles.selectedSport]: selectedSport === "Pickle Ball",
                  })}
                >
                  <div className={styles.iconContainer}>
                    <div className={styles.pickeBallSportCard} />
                  </div>
                  <span>Pickleball</span>
                </button>
              )}
            </div>
          </div>
          <h3 className={styles.sectionHeader}>Select Playing Date:</h3>
          <Calendar value={date} setValue={dateChangeHandler} />
          <div className={styles.date}>{date.format("DD MMM, YYYY")}</div>
        </div>
        <div className={styles.turfContainer}>
          {fetchingBookNowDetail && <Loader size="large" />}

          {!fetchingBookNowDetail && bookingAllowed ? (
            <>
              <div>
                <div className={styles.slotContainer}>
                  <h3 className={styles.sectionHeader}>Select Slot:</h3>
                  <div className={styles.slots}>
                    {(allSlots || []).map(
                      ({ selected = false, time, cost }) => (
                        <button
                          key={time}
                          onClick={() => slotClickHandler(time)}
                          className={cx(styles.slot, styles.available, {
                            [styles.selectedTime]: selected,
                          })}
                          disabled={shouldDisableSlotTime(time)}
                        >
                          {timeFormat(getTimeSlot(time))}
                        </button>
                      ),
                    )}
                  </div>
                </div>

                <div className={styles.durationContainer}>
                  <h3 className={styles.sectionHeader}>Select Duration:</h3>

                  <div>
                    <Button
                      onClick={() => durationBtnClickHandler(-1)}
                      style={btnStyle}
                      disabled={duration === 1}
                    >
                      <strong>-</strong>
                    </Button>
                    <span>{duration} hour(s)</span>
                    <Button
                      style={btnStyle}
                      onClick={() => durationBtnClickHandler(1)}
                      disabled={
                        !selectedSlot || duration >= getMaxAvailableDuration()
                      }
                    >
                      <strong>+</strong>
                    </Button>
                  </div>
                </div>
              </div>
              <h3 className={styles.sectionHeader}>
                Select {fieldMappingAgainstSport[sportMapping[selectedSport]]}:
              </h3>

              <div className={styles.availableTurf}>
                {selectedSport === "Pickle Ball" && (
                  // Code for Pickle Ball - dynamically generated based on location mapping
                  <>
                    {getCourtsForLocation(selectedLocation, "Pickle Ball").map(
                      (court) => (
                        <button
                          key={court.turfKey}
                          onClick={() => setSelectedTurf(court.turfKey)}
                          className={cx(styles.turfCard, {
                            [styles.selectedTurf]:
                              selectedTurf === court.turfKey,
                          })}
                          disabled={
                            !isSlotAvailable(
                              slotsAvailability[court.turfKey],
                              selectedSlot,
                              duration,
                            ) || !isTurfPricingAvailable(court.turfKey)
                          }
                        >
                          {court.displayName}
                        </button>
                      ),
                    )}
                  </>
                )}

                {selectedSport === "Cricket Net" && (
                  // Code for Cricket Net
                  <>
                    <button
                      onClick={() => setSelectedTurf("turfA")}
                      className={cx(styles.turfCard, {
                        [styles.selectedTurf]: selectedTurf === "turfA",
                      })}
                      disabled={
                        !isSlotAvailable(
                          slotsAvailability.turfA,
                          selectedSlot,
                          duration,
                        ) || !isTurfPricingAvailable("turfA")
                      }
                    >
                      Net A (3 Players Max)
                    </button>

                    <button
                      onClick={() => setSelectedTurf("turfC")}
                      className={cx(styles.turfCard, {
                        [styles.selectedTurf]: selectedTurf === "turfC",
                      })}
                      disabled={
                        !isSlotAvailable(
                          slotsAvailability.turfC,
                          selectedSlot,
                          duration,
                        ) || !isTurfPricingAvailable("turfC")
                      }
                    >
                      Net A (6 Players Max)
                    </button>

                    <button
                      onClick={() => setSelectedTurf("turfB")}
                      className={cx(styles.turfCard, {
                        [styles.selectedTurf]: selectedTurf === "turfB",
                      })}
                      disabled={
                        !isSlotAvailable(
                          slotsAvailability.turfB,
                          selectedSlot,
                          duration,
                        ) || !isTurfPricingAvailable("turfB")
                      }
                    >
                      Net B (3 Players Max)
                    </button>

                    <button
                      onClick={() => setSelectedTurf("turfD")}
                      className={cx(styles.turfCard, {
                        [styles.selectedTurf]: selectedTurf === "turfD",
                      })}
                      disabled={
                        !isSlotAvailable(
                          slotsAvailability.turfD,
                          selectedSlot,
                          duration,
                        ) || !isTurfPricingAvailable("turfD")
                      }
                    >
                      Net B (6 Players Max)
                    </button>
                  </>
                )}

                {["Football", "Box Cricket"].includes(selectedSport) && (
                  // Code for Box Cricket & Football
                  <>
                    <button
                      onClick={() => setSelectedTurf("turfA")}
                      className={cx(styles.turfCard, {
                        [styles.selectedTurf]: selectedTurf === "turfA",
                      })}
                      disabled={
                        !isSlotAvailable(
                          slotsAvailability.turfA,
                          selectedSlot,
                          duration,
                        ) || !isTurfPricingAvailable("turfA")
                      }
                    >
                      5 a side Turf A
                    </button>

                    <button
                      onClick={() => setSelectedTurf("turfB")}
                      className={cx(styles.turfCard, {
                        [styles.selectedTurf]: selectedTurf === "turfB",
                      })}
                      disabled={
                        !isSlotAvailable(
                          slotsAvailability.turfB,
                          selectedSlot,
                          duration,
                        ) || !isTurfPricingAvailable("turfB")
                      }
                    >
                      5 a side Turf B
                    </button>

                    <button
                      onClick={() => setSelectedTurf("turfC")}
                      className={cx(styles.turfCard, {
                        [styles.selectedTurf]: selectedTurf === "turfC",
                      })}
                      disabled={
                        !isSlotAvailable(
                          slotsAvailability.turfC,
                          selectedSlot,
                          duration,
                        ) || !isTurfPricingAvailable("turfC")
                      }
                    >
                      7 a side Turf C
                    </button>
                  </>
                )}
              </div>

              {/* Equipment Selection for Pickleball - Only for Srinagar location */}
              {selectedSport === "Pickle Ball" &&
                selectedTurf &&
                (selectedLocation?.toLowerCase() === "srinagar" ||
                  selectedLocation?.toLowerCase() === "jammu") &&
                equipmentConfigs.length > 0 && (
                  <div className={styles.equipmentContainer}>
                    <h3 className={styles.sectionHeader}>Select Equipment:</h3>

                    {loadingEquipment ? (
                      <div className={styles.loadingEquipment}>
                        Loading equipment options...
                      </div>
                    ) : (
                      <div className={styles.equipmentSection}>
                        {equipmentConfigs.map((equipment) => {
                          const isPaddle = equipment.name === "Paddle";
                          const isBall = equipment.name === "Ball";
                          const currentQuantity = isPaddle
                            ? paddleQuantity
                            : isBall
                              ? ballQuantity
                              : 0;
                          const maxQuantity = equipment.max_quantity;

                          return (
                            <div
                              key={equipment.id}
                              className={styles.equipmentItem}
                            >
                              <span className={styles.equipmentLabel}>
                                {equipment.name} (₹{equipment.unit_price} each)
                              </span>
                              <div className={styles.quantityControl}>
                                <Button
                                  onClick={() => {
                                    if (isPaddle)
                                      handlePaddleQuantityChange(-1);
                                    if (isBall) handleBallQuantityChange(-1);
                                  }}
                                  style={btnStyle}
                                  disabled={
                                    currentQuantity === (isPaddle ? 0 : 0)
                                  }
                                >
                                  <strong>-</strong>
                                </Button>
                                <span className={styles.quantity}>
                                  {currentQuantity}
                                </span>
                                <Button
                                  onClick={() => {
                                    if (isPaddle) handlePaddleQuantityChange(1);
                                    if (isBall) handleBallQuantityChange(1);
                                  }}
                                  style={btnStyle}
                                  disabled={currentQuantity === maxQuantity}
                                >
                                  <strong>+</strong>
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

              {selectedTurf && (
                <span className={styles.costMobileScreen}>
                  <span>Booking Fee </span>
                  <span>{getBaseCost()} &#8377;</span>
                  {selectedSport === "Pickle Ball" &&
                    getEquipmentCost() > 0 && (
                      <>
                        <span>Equipment Fee </span>
                        <span>{getEquipmentCost()} &#8377;</span>
                      </>
                    )}
                  <span>Convenience Fee </span>
                  <span>{Math.ceil((totalCost() * 1) / 100)} &#8377;</span>
                  <strong>Total </strong>
                  <strong>{costWithExtraFee()} &#8377;</strong>
                </span>
              )}
              <div className={styles.finalSlot}>
                {selectedSlot ? timeFormat(getFinalSlot()) : "Select Slot"}
              </div>
            </>
          ) : !fetchingBookNowDetail && !bookingAllowed ? (
            <span className={styles.msg}>
              Bookings are not allowed for the selected date.
            </span>
          ) : (
            <div className={styles.loadingEquipment}>
              Loading booking details...
            </div>
          )}
        </div>
      </div>
      <div className={styles.btnContainer}>
        {!login && (
          <button
            className={styles.btn}
            onClick={() => submitHandler(0)}
            disabled={disableBtn() || newBookingInProgress}
          >
            {newBookingInProgress ? <LoaderForButton /> : "LOGIN TO CONTINUE"}
          </button>
        )}
      </div>

      <div className={styles.btnContainer}>
        {login && (
          <button
            className={styles.btn}
            onClick={() => submitHandler(0)}
            disabled={disableBtn() || newBookingInProgress}
          >
            {newBookingInProgress ? <LoaderForButton /> : "PROCEED TO PAY"}
          </button>
        )}
      </div>

      {login &&
        totalCost() > 700 &&
        selectedSport !== "Pickle Ball" &&
        selectedSport !== "Cricket Net" && (
          <div className={styles.btnContainer}>
            <button
              className={styles.btn}
              onClick={() => submitHandler(1)}
              disabled={disableBtn() || newBookingInProgress}
            >
              {newBookingInProgress ? <LoaderForButton /> : btn_partial_txt}
            </button>
          </div>
        )}
    </>
  );
};

export default BookNow;
